import { Program, ProgramStatus, ProgramType } from '../../../swagger'
import { ProgramFeeNames } from '../../Programs/ProgramFeeFields'
import { ProgramFormValidFields } from './types'

export const daysOfTheWeekMap = new Map([
  ['Sunday', 0],
  ['Monday', 1],
  ['Tuesday', 2],
  ['Wednesday', 3],
  ['Thursday', 4],
  ['Friday', 5],
  ['Saturday', 6],
])

const today = new Date()

export const MAXIMUM_SEMESTER_DATE = new Date(
  Date.UTC(today.getFullYear() + 2, 11, 31)
)

export const MINIMUM_SEMESTER_DATE = new Date(
  Date.UTC(today.getFullYear() - 2, 0, 1)
)

/**
 * validFieldsInitialState: defines form `validFields` initial state
 * is used to restore field validations once the user cancels editing,
 * and is modified as we handle change events on `handleInputChange`
 */
export const validFieldsInitialState: ProgramFormValidFields = {
  type: { input: true },
  semesterOneStartDate: {
    input: true,
    beforeMax: true,
    afterMin: true,
  },
  semesterTwoStartDate: {
    input: true,
    beforeMax: true,
    afterMin: true,
    afterPrevious: true,
  },
  startTime: { input: true },
  endTime: {
    input: true,
    afterPrevious: true,
  },
  dayOfTheWeek: { input: true },
  capacity: { input: true },
  director: { input: true },
  tutorName: { input: true },
  tutorActorId: { input: true },
  communityName: { input: true },
  directorPhone: { input: true },
  tutorModel: { input: true },
  [ProgramFeeNames.ApplicationFee]: { input: true },
  [ProgramFeeNames.Tuition]: { input: true },
  [ProgramFeeNames.EnrollmentFee]: { input: true },
  [ProgramFeeNames.SupplyFeeFirstStudent]: { input: true },
  [ProgramFeeNames.SupplyFeeAdditionalStudent]: { input: true },
  [ProgramFeeNames.LocalFeeFirstStudent]: { input: true },
  [ProgramFeeNames.LocalFeeAdditionalStudent]: { input: true },
  [ProgramFeeNames.FacilityFeeFirstStudent]: { input: true },
  [ProgramFeeNames.FacilityFeeAdditionalStudent]: { input: true },
  [ProgramFeeNames.MiscFeeFirstStudent]: { input: true },
  [ProgramFeeNames.MiscFeeAdditionalStudent]: { input: true },
  [ProgramFeeNames.SubLicensedTutorPercentage]: { input: true },
  [ProgramFeeNames.SemesterOneLicensingFee]: { input: true },
  [ProgramFeeNames.DiscountSemesterOneLicensingFee]: { input: true },
  [ProgramFeeNames.SemesterTwoLicensingFee]: { input: true },
  [ProgramFeeNames.DiscountSemesterTwoLicensingFee]: { input: true },
  [ProgramFeeNames.MultiStudentApplicationDiscount]: { input: true },
  [ProgramFeeNames.InvitationFee]: { input: true },
  [ProgramFeeNames.DiscountInvitationFee]: { input: true },
  [ProgramFeeNames.TutorS1FirstStudentLicFee]: { input: true },
  [ProgramFeeNames.TutorS1AdditionalStudentLicFee]: { input: true },
  [ProgramFeeNames.TutorS2FirstStudentLicFee]: { input: true },
  [ProgramFeeNames.TutorS2AdditionalStudentLicFee]: { input: true },
  [ProgramFeeNames.LcdTutorPercentage]: { input: true },
}

/**
 * emptyProgramDetails: Used when adding a new Program or resetting a fields upon cancel when in Add variant.
 * When using this object in tests, it is prudent to spread it to another object
 * so we start with the same fresh values and do not mutate for different tests.
 */
export const emptyProgramDetails: Program = {
  programsKey: -1,
  type: ProgramType.Foundations,
  status: ProgramStatus.Draft,
  semesterOneStartDate: new Date(),
  semesterTwoStartDate: new Date(),
  startTime: '08:00',
  endTime: '15:00',
  dayOfTheWeek: 0,
  director: '',
  directorEmail: '',
  directorActorId: -1,
  tutorName: '',
  tutorEmail: '',
  tutorActorId: -1,
  capacity: 0,
  currencyCode: '',
  applicationFee: undefined,
  enrollmentFee: undefined,
  supplyFeeFirstStudent: undefined,
  supplyFeeAdditionalStudent: undefined,
  tuition: undefined,
  localFeeFirstStudent: undefined,
  localFeeAdditionalStudent: undefined,
  facilityFeeFirstStudent: undefined,
  facilityFeeAdditionalStudent: undefined,
  miscFeeFirstStudent: undefined,
  miscFeeAdditionalStudent: undefined,
  subLicensedTutorPercentage: undefined,
  semesterOneLicensingFee: undefined,
  discountSemesterOneLicensingFee: undefined,
  semesterTwoLicensingFee: undefined,
  discountSemesterTwoLicensingFee: undefined,
  multiStudentApplicationDiscount: undefined,
  invitationFee: undefined,
  discountInvitationFee: undefined,
  communityId: -1,
  communityName: '',
  address: {
    locationName: '',
    streetAddress1: '',
    streetAddress2: '',
    city: '',
    state: '',
    zip: '',
    countryCode: '',
  },
  availableSpots: 9,
  meta: undefined, // No initial meta
  tutorS1FirstStudentLicFee: undefined,
  tutorS1AdditionalStudentLicFee: undefined,
  tutorS2FirstStudentLicFee: undefined,
  tutorS2AdditionalStudentLicFee: undefined,
  tutorModel: '',
  lcdTutorPercentage: undefined,
}
